import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/use-auth'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

export const Route = createFileRoute('/_layout/')({
  component: DashboardPage,
})

function DashboardPage() {
  const { t } = useTranslation()
  const { user } = useAuth()

  return (
    <div className="space-y-6 lg:space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold">{t('dashboard.title')}</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {t('dashboard.welcome')}, {user?.email?.split('@')[0]}
        </p>
      </div>

      {/* Stats row - 2 cols on mobile, 4 on desktop */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <StatCard title={t('dashboard.totalBalance')} value="$124,500" change="+2.4%" positive />
        <StatCard title={t('dashboard.activeTrades')} value="12" change="+3" positive />
        <StatCard title={t('dashboard.pnlToday')} value="+$2,840" change="+1.2%" positive />
        <StatCard title={t('dashboard.openOrders')} value="5" change="-2" positive={false} />
      </div>

      {/* Main content area - stack on mobile, side by side on desktop */}
      <div className="grid gap-4 sm:gap-6 lg:grid-cols-3">
        {/* Recent activity */}
        <div className="lg:col-span-2">
          <div className="rounded-xl border border-border bg-card">
            <div className="border-b border-border px-4 py-3 sm:px-6 sm:py-4">
              <h2 className="font-semibold text-foreground">{t('dashboard.recentActivity')}</h2>
            </div>
            <div className="p-4 sm:p-6">
              <p className="text-sm text-muted-foreground">{t('dashboard.noActivity')}</p>
            </div>
          </div>
        </div>

        {/* Quick actions */}
        <div>
          <div className="rounded-xl border border-border bg-card">
            <div className="border-b border-border px-4 py-3 sm:px-6 sm:py-4">
              <h2 className="font-semibold text-foreground">{t('dashboard.quickActions')}</h2>
            </div>
            <div className="p-4 sm:p-6">
              <div className="space-y-3">
                <Button variant="outline" className="w-full">
                  {t('dashboard.newTrade')}
                </Button>
                <Button variant="outline" className="w-full">
                  {t('dashboard.viewReports')}
                </Button>
                <Button variant="outline" className="w-full">
                  {t('nav.settings')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function StatCard({
  title,
  value,
  change,
  positive,
}: {
  title: string
  value: string
  change: string
  positive: boolean
}) {
  const { t } = useTranslation()

  return (
    <div className="rounded-xl border border-border bg-card p-3 sm:p-6">
      <p className="text-xs font-medium text-muted-foreground sm:text-sm">{title}</p>
      <p className="mt-1 text-lg font-semibold text-foreground sm:mt-2 sm:text-2xl">{value}</p>
      <p
        className={`mt-0.5 text-xs sm:mt-1 sm:text-sm ${
          positive ? 'text-primary' : 'text-destructive'
        }`}
      >
        {change}
        <span className="hidden sm:inline"> {t('dashboard.fromLastWeek')}</span>
      </p>
    </div>
  )
}
